import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import axios from 'axios';
import { APP_VERSION } from '../config/config';
import BusinessSwitcherModal from './BusinessSwitcherModal';
import Sidebar from './Sidebar';
import { FilterParams } from '../views/leads/leadsGrid/leads.types';
import GenericConfirmationModal from './GenericConfirmationModal';
import toast from 'react-hot-toast';
import { MailCheck } from 'lucide-react';

// Define proper types for better performance
interface HeaderProps {
  onSearch?: (value: string) => void;
  closeIconClick?: () => void;
  isSearch?: boolean;
  searchPlaceholder?: string;
  isOpen?: boolean;
  setIsOpen?: (value: boolean) => void;
}

// Static styles
const headerStyle = {
  height: "1px",
  padding: "0 10px",
};

// Memoize the BusinessSwitcherModal component
const MemoizedBusinessSwitcherModal = React.memo(BusinessSwitcherModal);

// Memoize the Sidebar component
const MemoizedSidebar = React.memo(Sidebar);

const emailPreview = `Subscribe to get your daily business report email like this from 3Sigma CRM:

📊 Daily Performance Summary
──────────────────
New Leads:  15 leads added
──────────────────
Calls Activity:
• Total Calls: 45 calls
• Connected: 32 calls
• Avg Call Time: 3.5 mins
──────────────────
Sales Updates:
• Total Sales: ₹75,000
• sales : 12 completed
──────────────────
Tasks:
• Due Today: 8 tasks
• Completed: 5 tasks
• Overdue: 2 tasks

Note: If you've already subsribed for daily email reports once no need do it again, no need to subscribe again.`;

const Header: React.FC<HeaderProps> = ({
  onSearch,
  closeIconClick,
  isSearch,
  searchPlaceholder,
  isOpen,
  setIsOpen,
}) => {
  // Get filter params from Redux with proper typing
  const { filterParam }: { filterParam: FilterParams } = useSelector(
    (state: any) => state.rootReducers.leads
  );
  
  // Component state
  const [filterval, setfilterval] = useState<string>("");
  const [showSwitcherModal, setShowSwitcherModal] = useState(false);
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
  const [emailPrefLoading, setEmailPrefLoading] = useState(false);
  
  // Initialize userdata from localStorage using lazy initialization
  const [userdata, setUserdata] = useState(() => {
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error("Error parsing user data", e);
      return {};
    }
  });
  
  const navigate = useNavigate();

  // Create a properly memoized debounced search function
  const debouncedSearch = useCallback(
    _.debounce((value: string) => {
      onSearch && onSearch(value);
    }, 500),
    [onSearch]
  );

  // Clean up debounce function when component unmounts
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle search input change
  const handleChange = useCallback((propogate?: boolean) => {
    setIsOpen && setIsOpen(!isOpen);
    if (propogate) {
      closeIconClick && closeIconClick();
    }
  }, [setIsOpen, isOpen, closeIconClick]);

  // Handle logout
  const handleLogout = useCallback(() => {
    localStorage.clear();
    setTimeout(() => {
      navigate("/");
      window.location.reload();
    }, 1000);
  }, [navigate]);

  // Handle logout with message for unauthorized access
  const handleUnauthorizedLogout = useCallback(() => {
    // Show a friendly message to the user
    toast.error(
      'You are being logged out. This may be due to:\n• Unauthorized access\n• Usage on more than one device at a time\n• Session expired',
      {
        duration: 3000,
        style: {
          background: '#ef4444',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px',
        },
        icon: '🔒',
      }
    );

    // Clear storage and logout after showing the message
    localStorage.clear();
    setTimeout(() => {
      navigate("/");
      window.location.reload();
    }, 3000); // 3 second delay to show the message
  }, [navigate]);

  // Set up axios interceptor once during component mount
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleUnauthorizedLogout();
        }
        return Promise.reject(error);
      }
    );

    // Return cleanup function
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [handleUnauthorizedLogout]);

  // Business switcher click handler
  const handleSwitchClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dropdown = document.querySelector('.user-info-dropdown-toggler');
    (dropdown as any)?.click();
    setShowSwitcherModal(true);
  }, []);

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowSwitcherModal(false);
  }, []);

  // Memoize sidebar component to prevent unnecessary re-renders
  const sidebarComponent = useMemo(() => (
    <MemoizedSidebar userdata={userdata} />
  ), [userdata]);

  // Memoize business switcher modal to prevent unnecessary re-renders
  const businessSwitcherModal = useMemo(() => (
    <MemoizedBusinessSwitcherModal
      isOpen={showSwitcherModal}
      onClose={handleCloseModal}
    />
  ), [showSwitcherModal, handleCloseModal]);

  return (
    <>
      <header
        id="header"
        className="header fixed-top d-flex align-items-center"
        style={headerStyle}
      >
        <nav className="header-nav ms-auto">
          {/* Navigation content here */}
        </nav>
      </header>

      {/* Render memoized sidebar */}
      {sidebarComponent}

      {/* Render memoized business switcher modal */}
      {businessSwitcherModal}
    </>
  );
};

// Export memoized component
export default React.memo(Header);